import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, Pagination, Select, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';
import { AuthorizationContext } from 'src/context/AuthorizationProvider';

import { Modal, Table } from '../Molecules';
import HistoryDetail from './HistoryDetail';

const HistoryList = () => {
  const { customerId } = useContext(AuthorizationContext);
  const { t } = useTranslation('admin', { keyPrefix: 'history' });

  // State management
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Filter states
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState(null);
  const [dateToFilter, setDateToFilter] = useState(null);
  const [bridgeIdFilter, setBridgeIdFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch history data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        page: page.toString()
      });

      if (customerId) params.append('customerId', customerId);
      if (userIdFilter) params.append('userId', userIdFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (dateFromFilter) {
        // Create start of day datetime and encode for URL
        const startOfDay = new Date(dateFromFilter);
        startOfDay.setHours(0, 0, 0, 0);
        params.append('dateFrom', encodeURIComponent(startOfDay.toISOString()));
      }
      if (dateToFilter) {
        // Create end of day datetime and encode for URL
        const endOfDay = new Date(dateToFilter);
        endOfDay.setHours(23, 59, 59, 999);
        params.append('dateTo', encodeURIComponent(endOfDay.toISOString()));
      }
      if (bridgeIdFilter) params.append('bridgeId', bridgeIdFilter);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/history?${params}`);
      const result = await response.json();

      if (result.successInd) {
        setData(result);
      } else {
        console.error('Error fetching history:', result.message);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, [page, customerId, userIdFilter, typeFilter, dateFromFilter, dateToFilter, bridgeIdFilter, limit]);

  // Fetch data when filters change
  useEffect(() => {
    fetchData();
  }, [page, customerId, userIdFilter, typeFilter, dateFromFilter, dateToFilter, bridgeIdFilter, fetchData]);

  // Handle view details
  // const handleViewDetails = async historyId => {
  //   try {
  //     const response = await fetch(`${process.env.REACT_APP_API_URL}/history/${historyId}`);
  //     const result = await response.json();

  //     if (result.successInd) {
  //       setSelectedEntry(result.data);
  //       setModalOpen(true);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching history details:', error);
  //   }
  // };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEntry(null);
  };

  // Table columns
  const columns = [
    { field: 'id', text: 'ID' },
    { field: 'date', text: t('columns.date') },
    { field: 'userName', text: t('columns.user') },
    { field: 'actionType', text: t('columns.action') },
    { field: 'bridgeName', text: t('columns.bridge') },
    { field: 'gateName', text: t('columns.gate') }
  ];

  // Format rows for table
  const rows =
    data?.data?.map(entry => {
      const formatDate = dateString => {
        try {
          return new Date(dateString).toLocaleString();
        } catch {
          return dateString;
        }
      };

      const getActionTypeDisplay = type => {
        switch (type) {
          case 'E':
            return t('actionTypes.entry');
          case 'S':
            return t('actionTypes.exit');
          case 'ES':
            return t('actionTypes.entryExit');
          default:
            return type;
        }
      };

      return {
        actionType: getActionTypeDisplay(entry.type),
        bridgeName: entry.bridge?.name || 'N/A',
        date: formatDate(entry.date),
        gateName: entry.gate?.name || 'N/A',
        id: entry.id,
        userName: entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'N/A'
      };
    }) || [];

  // Filter rows based on search term
  const filteredRows = rows.filter(
    row => !searchTerm || Object.values(row).some(value => value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box>
      {/* Filters Section */}
      <Box mb={3}>
        <Typography mb={2} variant="h6">
          {t('filterBy')}
        </Typography>

        <Grid container mb={2} spacing={2}>
          {/* Search */}
          <Grid item md={4} xs={12}>
            <TextField fullWidth label={t('search')} onChange={e => setSearchTerm(e.target.value)} size="small" value={searchTerm} />
          </Grid>

          {/* Type Filter */}
          <Grid item md={2} xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('columns.action')}</InputLabel>
              <Select label={t('columns.action')} onChange={e => setTypeFilter(e.target.value)} value={typeFilter}>
                <MenuItem value="">{t('actionTypes.all')}</MenuItem>
                <MenuItem value="E">{t('actionTypes.entry')}</MenuItem>
                <MenuItem value="S">{t('actionTypes.exit')}</MenuItem>
                <MenuItem value="ES">{t('actionTypes.entryExit')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* User ID Filter */}
          {/* <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="User ID"
              value={userIdFilter}
              onChange={e => setUserIdFilter(e.target.value)}
              size="small"
              placeholder="Enter user ID"
            />
          </Grid> */}

          {/* Bridge ID Filter */}
          {/* <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Bridge ID"
              value={bridgeIdFilter}
              onChange={e => setBridgeIdFilter(e.target.value)}
              size="small"
              placeholder="Enter bridge ID"
            />
          </Grid> */}

          {/* Date Range */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item md={3} xs={12}>
              <DatePicker
                format="dd/MM/yyyy"
                label={t('from')}
                onChange={setDateFromFilter}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                value={dateFromFilter}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <DatePicker
                format="dd/MM/yyyy"
                label={t('to')}
                onChange={setDateToFilter}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                value={dateToFilter}
              />
            </Grid>
          </LocalizationProvider>
        </Grid>

        {/* Active Filters Display */}
        {(typeFilter || userIdFilter || bridgeIdFilter || dateFromFilter || dateToFilter) && (
          <Box mb={2}>
            <Typography mb={1} variant="subtitle2">
              {t('activeFilters')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={1}>
              {typeFilter && <Chip label={`${t('columns.action')}: ${typeFilter}`} onDelete={() => setTypeFilter('')} size="small" />}
              {userIdFilter && <Chip label={`User: ${userIdFilter}`} onDelete={() => setUserIdFilter('')} size="small" />}
              {bridgeIdFilter && <Chip label={`Bridge: ${bridgeIdFilter}`} onDelete={() => setBridgeIdFilter('')} size="small" />}
              {dateFromFilter && (
                <Chip label={`${t('from')}: ${dateFromFilter.toLocaleDateString()}`} onDelete={() => setDateFromFilter(null)} size="small" />
              )}
              {dateToFilter && (
                <Chip label={`${t('to')}: ${dateToFilter.toLocaleDateString()}`} onDelete={() => setDateToFilter(null)} size="small" />
              )}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Results Info */}
      {data?.pagination && (
        <Box mb={2}>
          <Typography color="text.secondary" variant="body2">
            {t('viewDetails', { range: `${data.pagination.startEntry}-${data.pagination.endEntry}`, total: data.pagination.totalEntries })}
          </Typography>
        </Box>
      )}

      {/* Table */}
      <Table columns={columns} loading={loading} onView={null} rows={filteredRows} />

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            color="primary"
            count={data.pagination.totalPages}
            onChange={(event, value) => setPage(value)}
            page={page}
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* No Results */}
      {!loading && filteredRows.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary" variant="body1">
            {t('noRecordsFound')}
          </Typography>
        </Box>
      )}

      {/* Detail Modal */}
      <Modal handleClose={handleCloseModal} labels={{ title: t('actions.viewDetails') }} minWidth={600} open={modalOpen}>
        {selectedEntry && <HistoryDetail entry={selectedEntry} />}
      </Modal>
    </Box>
  );
};

export default HistoryList;
